import {DimensionSpec, DimensionStats} from '@intellimize/dash-shared';
import type {DesignerActionFromCreator} from '@packages/systems/designer/flux';
import type {ExperienceStatus} from '@packages/domains/optimize/logic';
import {
  ExperienceOptimizationJson,
  OptimizationJson,
  ScopedMetricJson,
} from '@packages/systems/optimization/types';
import {ActionType, createAction} from '@packages/utilities/create-action';
import {
  DateIntervalRequiredEnd,
  DateIntervalWithRange,
  FiltersState,
  IntegrationParams,
  ViewContext,
} from './types';

export const DATE_RANGE_CHANGED = 'DATE_RANGE_CHANGED' as const;
export const DATE_RANGE_RESET = 'DATE_RANGE_RESET' as const;
export const SELECTED_INTERVAL_UPDATED = 'SELECTED_INTERVAL_UPDATED' as const;
export const LOAD_EXPERIENCE_DETAILS = 'LOAD_EXPERIENCE_DETAILS' as const;
export const UPDATE_EXPERIENCE_ID = 'UPDATE_EXPERIENCE_ID' as const;
export const EXPERIENCE_LIST_UPDATE_STRING_FILTER =
  'EXPERIENCE_LIST_UPDATE_STRING_FILTER' as const;
export const EXPERIENCE_LIST_UPDATE_STATUS_FILTER =
  'EXPERIENCE_LIST_UPDATE_STATUS_FILTER' as const;
export const EXPERIENCE_LIST_UPDATE_COLUMN_VISIBILITY =
  'EXPERIENCE_LIST_UPDATE_COLUMN_VISIBILITY' as const;
export const OPTIMIZATION_AUTH_REQUESTED =
  'OPTIMIZATION_AUTH_REQUESTED' as const;
export const OPTIMIZATION_AUTH_SUCCEEDED =
  'OPTIMIZATION_AUTH_SUCCEEDED' as const;
export const OPTIMIZATION_AUTH_FAILED = 'OPTIMIZATION_AUTH_FAILED' as const;
export const INTELLIMIZE_AUTH_SUCCEEDED = 'INTELLIMIZE_AUTH_SUCCEEDED' as const;
export const DIMENSION_LIST_POPULATED = 'DIMENSION_LIST_POPULATED' as const;
export const DIMENSION_SPEC_SELECTED = 'DIMENSION_SPEC_SELECTED' as const;
export const FILTER_VALUES_POPULATED = 'FILTER_VALUES_POPULATED' as const;
export const FILTER_VALUES_SAVED = 'FILTER_VALUES_SAVED' as const;
export const SELECTED_DIMENSION_CLEARED = 'SELECTED_DIMENSION_CLEARED' as const;
export const SELECTED_GOAL_UPDATED = 'SELECTED_GOAL_UPDATED' as const;
export const OPTIMIZATION_IFRAME_LOAD_ERRORED =
  'OPTIMIZATION_IFRAME_LOAD_ERRORED' as const;
export const REPORT_PARAMS_RESET = 'REPORT_PARAMS_RESET' as const;
export const INTEGRATION_PARAMS_UPDATED = 'INTEGRATION_PARAMS_UPDATED' as const;
export const OPTIMIZATION_OVERVIEW_PARAMS_UPDATED =
  'OPTIMIZATION_OVERVIEW_PARAMS_UPDATED' as const;
export const VIEW_CONTEXT_UPDATED = 'VIEW_CONTEXT_UPDATED' as const;

export const handleViewContextUpdate = createAction<
  typeof VIEW_CONTEXT_UPDATED,
  {
    viewContext: ViewContext;
  }
>(VIEW_CONTEXT_UPDATED);
export const handleOptimizationOverviewParamsUpdate = createAction<
  typeof OPTIMIZATION_OVERVIEW_PARAMS_UPDATED,
  {
    selectedDateRange?: DateIntervalWithRange;
    selectedGoal?: ScopedMetricJson;
  }
>(OPTIMIZATION_OVERVIEW_PARAMS_UPDATED);
export const handleIntegrationParamChange = createAction<
  typeof INTEGRATION_PARAMS_UPDATED,
  {resetIntegrationParams?: IntegrationParams[]; resetAll?: boolean}
>(INTEGRATION_PARAMS_UPDATED);
export const resetReportParams = createAction<typeof REPORT_PARAMS_RESET, void>(
  REPORT_PARAMS_RESET
);
export const handleDateIntervalChange = createAction<
  typeof DATE_RANGE_CHANGED,
  {dateInterval: DateIntervalRequiredEnd}
>(DATE_RANGE_CHANGED);
export const handleSelectedIntervalChange = createAction<
  typeof SELECTED_INTERVAL_UPDATED,
  {index: number; optimizationJson: OptimizationJson | undefined}
>(SELECTED_INTERVAL_UPDATED);
export const resetSelectedDateRange = createAction<
  typeof DATE_RANGE_RESET,
  void
>(DATE_RANGE_RESET);
export const loadExperienceDetails = createAction<
  typeof LOAD_EXPERIENCE_DETAILS,
  {
    experience: ExperienceOptimizationJson;
    optimizationJson: OptimizationJson;
    urlParams: {
      experienceStart: string | null;
      experienceEnd: string | null;
      experienceGoals: string | null;
    };
  }
>(LOAD_EXPERIENCE_DETAILS);
export const updateExperienceId = createAction<
  typeof UPDATE_EXPERIENCE_ID,
  {experienceId: string | undefined}
>(UPDATE_EXPERIENCE_ID);
export const experienceListUpdateStringFilter = createAction<
  typeof EXPERIENCE_LIST_UPDATE_STRING_FILTER,
  {
    searchTerm: string;
  }
>(EXPERIENCE_LIST_UPDATE_STRING_FILTER);

export const experienceListUpdateStatusFilter = createAction<
  typeof EXPERIENCE_LIST_UPDATE_STATUS_FILTER,
  {
    status: ExperienceStatus | 'all';
  }
>(EXPERIENCE_LIST_UPDATE_STATUS_FILTER);

export const getOptimizationAuthToken = createAction<
  typeof OPTIMIZATION_AUTH_REQUESTED,
  {requestId: string}
>(OPTIMIZATION_AUTH_REQUESTED);
export const handleOptimizationAuthSucceeded = createAction<
  typeof OPTIMIZATION_AUTH_SUCCEEDED,
  {response: {authToken: string | null}}
>(OPTIMIZATION_AUTH_SUCCEEDED);
export const handleIntellimizeAuthSuccess = createAction<
  typeof INTELLIMIZE_AUTH_SUCCEEDED,
  void
>(INTELLIMIZE_AUTH_SUCCEEDED);
export const populateDimensionListItems = createAction<
  typeof DIMENSION_LIST_POPULATED,
  {
    dimensionList: DimensionSpec[];
    viewContext: ViewContext;
  }
>(DIMENSION_LIST_POPULATED);
export const handleDimensionSpecSelect = createAction<
  typeof DIMENSION_SPEC_SELECTED,
  {
    dimensionSpec: DimensionSpec;
    viewContext: ViewContext;
  }
>(DIMENSION_SPEC_SELECTED);
export const populateSelectedDimensionFilterValues = createAction<
  typeof FILTER_VALUES_POPULATED,
  {
    dimensionSpec: DimensionSpec;
    filterValues: DimensionStats[];
    viewContext: ViewContext;
  }
>(FILTER_VALUES_POPULATED);
export const saveFilterValues = createAction<
  typeof FILTER_VALUES_SAVED,
  {
    updatedAudienceFilters: FiltersState;
    viewContext: ViewContext;
  }
>(FILTER_VALUES_SAVED);
export const clearSelectedDimension = createAction<
  typeof SELECTED_DIMENSION_CLEARED,
  {
    viewContext: ViewContext;
  }
>(SELECTED_DIMENSION_CLEARED);
export const updateSelectedGoal = createAction<
  typeof SELECTED_GOAL_UPDATED,
  {
    selectedGoal: ScopedMetricJson;
    viewContext: ViewContext;
  }
>(SELECTED_GOAL_UPDATED);
export const optimizationIframeLoadErrored = createAction<
  typeof OPTIMIZATION_IFRAME_LOAD_ERRORED,
  {
    message: string;
    type: 'error' | 'warning';
  }
>(OPTIMIZATION_IFRAME_LOAD_ERRORED);

export type HandleIntegrationParamChangeAction = DesignerActionFromCreator<
  typeof handleIntegrationParamChange
>;

export type HandleDateIntervalChangeAction = ActionType<
  typeof handleDateIntervalChange
>;
export type HandleSelectedIntervalChangeAction = ActionType<
  typeof handleSelectedIntervalChange
>;
export type HydrateAnalyticsStateAction = DesignerActionFromCreator<
  typeof loadExperienceDetails
>;
export type ResetSelectedDateRangeAction = ActionType<
  typeof resetSelectedDateRange
>;
export type UpdateExperienceIdAction = ActionType<typeof updateExperienceId>;
export type ExperienceListUpdateStringFilterAction = ActionType<
  typeof experienceListUpdateStringFilter
>;

export type ExperienceListUpdateStatusFilterAction = ActionType<
  typeof experienceListUpdateStatusFilter
>;

export type GetOptimizationAuthTokenAction = DesignerActionFromCreator<
  typeof getOptimizationAuthToken
>;
export type HandleOptimizationAuthSucceededAction = DesignerActionFromCreator<
  typeof handleOptimizationAuthSucceeded
>;
export type HandleIntellimizeAuthSuccessAction = DesignerActionFromCreator<
  typeof handleIntellimizeAuthSuccess
>;

export type PopulateDimensionListItemsAction = ActionType<
  typeof populateDimensionListItems
>;
export type HandleDimensionSpecSelectAction = ActionType<
  typeof handleDimensionSpecSelect
>;
export type PopulateSelectedDimensionFilterValuesAction = ActionType<
  typeof populateSelectedDimensionFilterValues
>;
export type SaveFilterValuesAction = ActionType<typeof saveFilterValues>;
export type ClearSelectedDimensionAction = ActionType<
  typeof clearSelectedDimension
>;
export type UpdateSelectedGoalAction = DesignerActionFromCreator<
  typeof updateSelectedGoal
>;
export type UpdateOptimizationOverviewParamsAction = DesignerActionFromCreator<
  typeof handleOptimizationOverviewParamsUpdate
>;
export type UpdateViewContextAction = DesignerActionFromCreator<
  typeof handleViewContextUpdate
>;

export type AnalyticsActions =
  | ExperienceListUpdateStringFilterAction
  | ExperienceListUpdateStatusFilterAction
  | HandleDateIntervalChangeAction
  | HandleSelectedIntervalChangeAction
  | HydrateAnalyticsStateAction
  | ResetSelectedDateRangeAction
  | UpdateExperienceIdAction
  | ResetSelectedDateRangeAction
  | HandleOptimizationAuthSucceededAction
  | HandleIntellimizeAuthSuccessAction
  | GetOptimizationAuthTokenAction
  | PopulateDimensionListItemsAction
  | HandleDimensionSpecSelectAction
  | PopulateSelectedDimensionFilterValuesAction
  | SaveFilterValuesAction
  | ClearSelectedDimensionAction
  | UpdateSelectedGoalAction
  | UpdateOptimizationOverviewParamsAction
  | UpdateViewContextAction;
